import * as React from "react"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { cn } from "@/shared/utils/general/cn"

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/app/components/ui/dialog"

import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/app/components/ui/drawer"

// ---------------------------------------------------------------------------
// ResponsiveSheet — Dialog (desktop) / Drawer (mobile)
// ---------------------------------------------------------------------------

interface ResponsiveSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Visually-hidden title for accessibility */
  title?: string
  /** className applied to the content wrapper */
  className?: string
  children: React.ReactNode
}

/**
 * Responsive overlay component:
 * - **Desktop** (≥768px): Centered Radix Dialog (no bottom-to-top animation)
 * - **Mobile** (<768px): Vaul bottom Drawer (swipe-to-dismiss via handle)
 *
 * Both share the same children content.
 */
// Prevent Escape from propagating to parent handlers (e.g. TaskDetail close)
const stopEscape = (e: KeyboardEvent) => e.stopPropagation()

export function ResponsiveSheet({
  open,
  onOpenChange,
  title = "Sheet",
  className,
  children,
}: ResponsiveSheetProps) {
  const { isMobile } = useBreakpoint()

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} handleOnly>
        <DrawerContent
          className={cn(
            "mx-auto flex max-h-[85dvh] w-full flex-col",
            className,
          )}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Escape") e.stopPropagation()
          }}
        >
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          {children}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={stopEscape}
        className={cn(
          "flex max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-md",
          className,
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  )
}
